import React, { useState, useEffect } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2, ShoppingCart, Download, BarChart3, Package, Clock, Send, CheckCircle2, Search, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtCur = (v) => `€${Number(v || 0).toFixed(2)}`;
const statusBadge = (s) => {
  if (s === 'concept') return <Badge className="bg-slate-100 text-slate-700"><Clock className="w-3 h-3 mr-1" />Concept</Badge>;
  if (s === 'verzonden') return <Badge className="bg-blue-100 text-blue-700"><Send className="w-3 h-3 mr-1" />Verzonden</Badge>;
  if (s === 'ontvangen') return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Ontvangen</Badge>;
  if (s === 'deels_ontvangen') return <Badge className="bg-amber-100 text-amber-700"><Package className="w-3 h-3 mr-1" />Deels ontvangen</Badge>;
  if (s === 'probleem_gemeld') return <Badge className="bg-red-100 text-red-700"><Package className="w-3 h-3 mr-1" />Probleem gemeld</Badge>;
  return <Badge variant="outline">{s}</Badge>;
};

const BestellingenOverzicht = () => {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('overzicht');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rapportage, setRapportage] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [filters, setFilters] = useState({ locatie: 'alle', leverancier: 'alle', status: 'alle' });
  const [rapFilters, setRapFilters] = useState({ locatie: 'alle', leverancier: 'alle', jaar: String(new Date().getFullYear()) });
  const [sortCol, setSortCol] = useState('datum');
  const [sortDir, setSortDir] = useState('desc');
  // 4.4g: filters en paginering gaan naar de server; 200 stille records is voorbij
  const [pagina, setPagina] = useState(0);
  const [totaal, setTotaal] = useState(0);
  const PER_PAGINA = 50;

  useEffect(() => {
    Promise.all([
      fetch(`${API}/locaties`, { headers: hdrs() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/leveranciers-centraal`, { headers: hdrs() }).then(r => r.ok ? r.json() : []),
    ]).then(([locs, levs]) => { setLocaties(locs); setLeveranciers(levs); });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({
      gepagineerd: 'true', limit: String(PER_PAGINA), skip: String(pagina * PER_PAGINA),
    });
    if (filters.locatie !== 'alle') params.set('locatie_id', filters.locatie);
    if (filters.leverancier !== 'alle') params.set('leverancier_id', filters.leverancier);
    if (filters.status !== 'alle') params.set('status', filters.status);
    setLoading(true);
    fetch(`${API}/bestellingen/management/overzicht?${params}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : { rijen: [], totaal: 0 })
      .then(body => { setData(body.rijen || []); setTotaal(body.totaal || 0); })
      .finally(() => setLoading(false));
  }, [filters, pagina]);

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Weet je zeker dat je deze bestelling wilt verwijderen? Dit kan niet ongedaan worden.')) return;
    try {
      const res = await fetch(`${API}/bestellingen/${orderId}`, { method: 'DELETE', headers: hdrs() });
      if (res.ok) {
        toast.success('Bestelling verwijderd');
        setData(d => d.filter(o => o.id !== orderId));
        setTotaal(t => Math.max(0, t - 1));
      }
      else toast.error('Verwijderen mislukt');
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const fetchRapportage = () => {
    const params = new URLSearchParams();
    if (rapFilters.locatie !== 'alle') params.set('locatie_id', rapFilters.locatie);
    if (rapFilters.leverancier !== 'alle') params.set('leverancier_id', rapFilters.leverancier);
    if (rapFilters.jaar) params.set('jaar', rapFilters.jaar);
    fetch(`${API}/bestellingen/management/rapportage?${params}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : []).then(setRapportage).catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'rapportage') fetchRapportage(); }, [tab, rapFilters]);

  const handleCsvExport = () => {
    const params = new URLSearchParams();
    if (rapFilters.jaar) params.set('jaar', rapFilters.jaar);
    if (rapFilters.locatie !== 'alle') params.set('locatie_id', rapFilters.locatie);
    if (rapFilters.leverancier !== 'alle') params.set('leverancier_id', rapFilters.leverancier);
    // R8: de CSV-route vereist login → met Bearer-token ophalen en lokaal downloaden
    fetch(`${API}/bestellingen/management/rapportage/csv?${params}`, { headers: hdrs() })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bestellingen_rapportage_${rapFilters.jaar || 'alle'}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('CSV downloaden mislukt'));
  };

  // De server filtert al (4.4g); hier alleen sorteren binnen de huidige pagina
  const sorted = [...data].sort((a, b) => {
    let va = a[sortCol], vb = b[sortCol];
    if (sortCol === 'totaal_bedrag') { va = va || 0; vb = vb || 0; }
    if (sortCol === 'datum') { va = new Date(va || 0); vb = new Date(vb || 0); }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-5 max-w-6xl mx-auto px-4 sm:px-0" data-testid="bestellingen-management-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" /> Bestellingen Management
        </h1>
        <p className="text-sm text-slate-500">Alle bestellingen over alle locaties</p>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        <button onClick={() => setTab('overzicht')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'overzicht' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500'}`}>
          <Package className="w-4 h-4 inline mr-1" /> Overzicht
        </button>
        <button onClick={() => setTab('rapportage')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'rapportage' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500'}`}>
          <BarChart3 className="w-4 h-4 inline mr-1" /> Rapportage
        </button>
      </div>

      {tab === 'overzicht' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap bg-white rounded-xl border p-3">
            <Select value={filters.locatie} onValueChange={v => { setPagina(0); setFilters(p => ({ ...p, locatie: v })); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Locatie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle locaties</SelectItem>
                {locaties.map(l => <SelectItem key={l._id} value={l._id}>{l.naam}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.leverancier} onValueChange={v => { setPagina(0); setFilters(p => ({ ...p, leverancier: v })); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Leverancier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle leveranciers</SelectItem>
                {leveranciers.map(l => <SelectItem key={l._id} value={l._id}>{l.bedrijfsnaam}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={v => { setPagina(0); setFilters(p => ({ ...p, status: v })); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle statussen</SelectItem>
                <SelectItem value="concept">Concept</SelectItem>
                <SelectItem value="verzonden">Verzonden</SelectItem>
                <SelectItem value="ontvangen">Ontvangen</SelectItem>
                <SelectItem value="deels_ontvangen">Deels ontvangen</SelectItem>
                <SelectItem value="probleem_gemeld">Probleem gemeld</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 self-center" data-testid="bestellingen-totaal">
              {totaal} bestellingen
            </span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="management-orders-table">
                <thead>
                  <tr className="bg-slate-50 border-b text-left">
                    <th className="py-3 px-4 font-semibold text-slate-700 cursor-pointer" onClick={() => toggleSort('locatie_naam')}>Locatie</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 cursor-pointer" onClick={() => toggleSort('leverancier_naam')}>Leverancier</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 cursor-pointer" onClick={() => toggleSort('datum')}>Datum</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 text-center">Items</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 text-right cursor-pointer" onClick={() => toggleSort('totaal_bedrag')}>Totaal</th>
                    <th className="py-3 px-4 font-semibold text-slate-700">Status</th>
                    {isAdmin() && <th className="py-3 px-2 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {sorted.map(o => (
                    <tr key={o.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-700">{o.locatie_naam}</td>
                      <td className="py-3 px-4 text-slate-600">{o.leverancier_naam}</td>
                      <td className="py-3 px-4 text-slate-500">{fmtDate(o.datum)}</td>
                      <td className="py-3 px-4 text-center">{o.totaal_items}</td>
                      <td className="py-3 px-4 text-right font-bold text-[#1f2548]">{fmtCur(o.totaal_bedrag)}</td>
                      <td className="py-3 px-4">{statusBadge(o.status)}</td>
                      {isAdmin() && (
                        <td className="py-3 px-2">
                          <button onClick={() => handleDeleteOrder(o.id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`mgmt-delete-${o.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {sorted.length === 0 && <tr><td colSpan={isAdmin() ? 7 : 6} className="py-8 text-center text-slate-400">Geen bestellingen gevonden</td></tr>}
                </tbody>
              </table>
            </div>
            {totaal > PER_PAGINA && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">
                  {pagina * PER_PAGINA + 1}–{Math.min((pagina + 1) * PER_PAGINA, totaal)} van {totaal}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={pagina === 0}
                    onClick={() => setPagina(p => Math.max(0, p - 1))} data-testid="vorige-pagina">
                    Vorige
                  </Button>
                  <Button size="sm" variant="outline"
                    disabled={(pagina + 1) * PER_PAGINA >= totaal}
                    onClick={() => setPagina(p => p + 1)} data-testid="volgende-pagina">
                    Volgende
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'rapportage' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap bg-white rounded-xl border p-3">
            <Select value={rapFilters.jaar} onValueChange={v => setRapFilters(p => ({ ...p, jaar: v }))}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Jaar" /></SelectTrigger>
              <SelectContent>
                {[2026, 2025, 2024].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={rapFilters.locatie} onValueChange={v => setRapFilters(p => ({ ...p, locatie: v }))}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Locatie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle locaties</SelectItem>
                {locaties.map(l => <SelectItem key={l._id} value={l._id}>{l.naam}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={rapFilters.leverancier} onValueChange={v => setRapFilters(p => ({ ...p, leverancier: v }))}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Leverancier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle leveranciers</SelectItem>
                {leveranciers.map(l => <SelectItem key={l._id} value={l._id}>{l.bedrijfsnaam}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleCsvExport} data-testid="csv-export-btn">
              <Download className="w-4 h-4 mr-1" /> CSV Export
            </Button>
          </div>

          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rapportage-table">
                <thead>
                  <tr className="bg-slate-50 border-b text-left">
                    <th className="py-3 px-4 font-semibold text-slate-700">Product</th>
                    <th className="py-3 px-4 font-semibold text-slate-700">Leverancier</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 text-center">Totaal besteld</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 text-right">Totaal bedrag</th>
                    <th className="py-3 px-4 font-semibold text-slate-700 text-center">Bestellingen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {rapportage.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-700">{r.naam}</td>
                      <td className="py-3 px-4 text-slate-600">{r.leverancier}</td>
                      <td className="py-3 px-4 text-center">{r.totaal_aantal}</td>
                      <td className="py-3 px-4 text-right font-bold text-[#1f2548]">{fmtCur(r.totaal_bedrag)}</td>
                      <td className="py-3 px-4 text-center text-slate-500">{r.bestellingen}x</td>
                    </tr>
                  ))}
                  {rapportage.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Geen data voor deze periode</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestellingenOverzicht;
